import z from 'zod'

import { zodErrorParser } from '@/utils/zod'
import {
  AssetsResponseSchema,
  BaseEntitySchema,
  UserDetailsSchema
} from './Common'
import { SponsorshipResponseSchema } from './Sponsorship'
import { IndividualSocialPlatformGroupedResponseSchema, OrganizationUserResponseSchema } from './Organization'

const InnerCircleStatuses = [
  'ACTIVE',
  'INACTIVE',
] as const

const InnerCircleUserRoles = [
  'DELEGATE',
  'LAWYER',
  'FAMILY_MEMBER',
] as const

const SubjectDelegateResponseSchema = BaseEntitySchema.extend({
  assets: AssetsResponseSchema,
  details: UserDetailsSchema,
  email: z.string(),
  id: z.uuid(),
  isVerified: z.boolean(),
  sub: z.string().nullish(),
  subject: z.union([z.string(), z.object({ id: z.string() })]).nullish(),
}).strict()

const SubjectGroupResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  owner: z.union([
    z.uuid(),
    BaseEntitySchema.extend({
      id: z.uuid(),
      email: z.string().nullish(),
      isVerified: z.boolean().nullish(),
      details: z.uuid().nullish(),
      subject: z.any().nullish(),
      organization: z.array(z.uuid()).nullish(),
      assets: z.array(z.any()).nullish(),
    }).strict(),
  ]).nullish(),
  organization: z.union([
    z.uuid(),
    BaseEntitySchema.extend({
      id: z.uuid(),
      organizationDetails: z.uuid().nullish(),
      owner: z.uuid().nullish(),
    }).strict()
  ]).nullish(),
  name: z.string()
}).strict()

export type SubjectGroupResponse = z.infer<typeof SubjectGroupResponseSchema>

export const zodSubjectGroupResponseValidator = (
  data: SubjectGroupResponse[]
) => zodErrorParser(
  'SubjectGroupResponse',
  SubjectGroupResponseSchema,
  data,
)

const SubjectDetailsResponseSchema = BaseEntitySchema.extend({
  id: z.uuid().nullish(),
  occupation: z.string().nullish(),
  social: IndividualSocialPlatformGroupedResponseSchema.nullish(),
  group: BaseEntitySchema.extend({
    id: z.uuid(),
    name: z.string(),
    organization: z.uuid(),
    owner: z.uuid(),
  }).nullish(),
}).strict()

export type SubjectDetailsResponse = z.infer<typeof SubjectDetailsResponseSchema>

export const SubjectUserResponseSchema = BaseEntitySchema.extend({
  assets: AssetsResponseSchema.nullish(),
  delegates: z.array(SubjectDelegateResponseSchema).nullish(),
  details: UserDetailsSchema,
  email: z.string(),
  id: z.uuid(),
  isVerified: z.boolean(),
  sub: z.string().nullish(),
  subject: z.union([z.string(), z.object({ id: z.string() })]).nullish(),
  verifiedProfile: z.object().nullish(),
  organization: OrganizationUserResponseSchema.array().nullish(),
}).strict()

const InnerCircleResponseSchema = z.object({
  status: z.enum(InnerCircleStatuses),
  type: z.enum(InnerCircleUserRoles),
  user: SubjectUserResponseSchema,
}).strict()

export type InnerCircleResponse = z.infer<typeof InnerCircleResponseSchema>

export const SubjectResponseSchema = BaseEntitySchema.extend({
  alertsCount: z.union([z.number(), z.string()]).nullish(),
  alias: z.array(z.string()).nullish(),
  details: SubjectDetailsResponseSchema,
  group: SubjectGroupResponseSchema.nullish(),
  id: z.uuid(),
  innerCircle: z.array(InnerCircleResponseSchema).nullish(),
  user: SubjectUserResponseSchema,
  sponsorships: SponsorshipResponseSchema.array().nullish()
}).strict()

export type SubjectResponse = z.infer<typeof SubjectResponseSchema>

export const zodSubjectResponseValidator = (
  data: SubjectResponse[]
) => zodErrorParser(
  'SubjectResponse',
  SubjectResponseSchema,
  data,
)

const AssignedSubjectResponseSchema = SubjectResponseSchema.extend({
  details: z.object({
    occupation: z.string().nullish(),
    social: IndividualSocialPlatformGroupedResponseSchema.nullish(),
  }).strict(),
  group: SubjectGroupResponseSchema,
  user: SubjectUserResponseSchema
})

export type AssignedSubjectResponse = z.infer<typeof AssignedSubjectResponseSchema>

export const zodAssignedSubjectResponseValidator = (
  data: AssignedSubjectResponse[]
) => zodErrorParser(
  'AssignedSubjectResponse',
  AssignedSubjectResponseSchema,
  data,
)
